export const LOCALE = {
  PL: 'pl-PL',
  SK: 'sk-SK',
  CZ: 'cs-CZ',
  RO: 'ro-RO',
  DE: 'de-DE',
} as const;

export const CURRENCY = {
  PLN: 'PLN',
  EUR: 'EUR',
  CZK: 'CZK',
  RON: 'RON',
} as const;

export const LANGUAGE_CODE = {
  PL: 'pl',
  SK: 'sk',
  CZ: 'cz',
  RO: 'ro',
  DE: 'de',
} as const;

export const HTML_LANGUAGE_CODE: Record<I18nLanguageCode, string> = {
  pl: 'pl',
  sk: 'sk',
  cz: 'cs',
  ro: 'ro',
  de: 'de',
} as const;

export const i18nDefaultLanguageCode = LANGUAGE_CODE.PL;

export const i18nLocales = Object.values(LOCALE);
export type I18nLocale = (typeof i18nLocales)[number];

export const i18nLanguageCodes = Object.values(LANGUAGE_CODE);
export type I18nLanguageCode = (typeof i18nLanguageCodes)[number];

export const i18nMappings = [
  {
    code: LANGUAGE_CODE.PL,
    locale: LOCALE.PL,
  },
  {
    code: LANGUAGE_CODE.SK,
    locale: LOCALE.SK,
  },
  {
    code: LANGUAGE_CODE.CZ,
    locale: LOCALE.CZ,
  },
  {
    code: LANGUAGE_CODE.RO,
    locale: LOCALE.RO,
  },
  {
    code: LANGUAGE_CODE.DE,
    locale: LOCALE.DE,
  },
];

export function getCurrentLocaleByHost(host: string): I18nLocale {
  const hostParts = host.replace('www.', '').split('.');
  let languageCode: string | null = null;

  if (hostParts.length === 3) {
    // for example sk.finesio.test
    languageCode = hostParts?.[0];
  } else if (hostParts.length === 2) {
    // for example finesio.sk
    languageCode = hostParts?.[1];
  }

  if (!languageCode) throw new Error('Misconfigured i18n settings');

  const locale = i18nMappings.find(
    (config) => config.code === languageCode
  )?.locale;
  if (!locale) throw new Error('Unsupported i18n region');

  return locale;
}

export function getLanguageCodeByLocale(locale: string): I18nLanguageCode {
  const languageCode = i18nMappings.find(
    (config) => config.locale === locale
  )?.code;
  if (!languageCode) throw new Error('Unsupported i18n region');

  return languageCode;
}

export function getDeliveryCountry(locale: I18nLocale): string {
  switch (locale) {
    case LOCALE.PL:
      return 'PL';
    case LOCALE.SK:
      return 'SK';
    case LOCALE.CZ:
      return 'CZ';
    case LOCALE.RO:
      return 'RO';
    case LOCALE.DE:
      return 'DE';
    default:
      throw new Error(`Unknown locale: ${locale}`);
  }
}

export function getHtmlLanguageCode(locale: I18nLocale): string {
  switch (locale) {
    case LOCALE.PL:
      return 'pl';
    case LOCALE.SK:
      return 'sk';
    case LOCALE.CZ:
      return 'cs';
    case LOCALE.RO:
      return 'ro';
    case LOCALE.DE:
      return 'de';
    default:
      throw new Error(`Unknown locale: ${locale}`);
  }
}

export function getStorePreviewUrl(
  locale: string,
  environment: string
): string {
  switch (environment) {
    case 'production':
      return `https://finesio.${getLanguageCodeByLocale(locale)}`;
    case 'staging':
      return `https://${getLanguageCodeByLocale(locale)}.finesio.test`;
    case 'local':
      return `https://${getLanguageCodeByLocale(locale)}.finesio.local`;
    case 'development':
      return 'http://localhost:3001';
    default:
      throw new Error(`Unknown environment: ${environment}`);
  }
}
